/** The learner's private notebook — typed notes, scoped server-side to the
 *  signed-in user. The backend refuses anyone else's note with a 404, so the
 *  UI never needs to reason about ownership. */
import { request } from "./http";

export interface Note {
  id: string;
  content: string;
  /** The learner's own tab. Null = the default one. */
  section: string | null;
  /** Set when the note was saved out of a lesson rather than typed. */
  source_label: string | null;
  source_path: string | null;
  created_at: string;
  updated_at: string | null;
}

export function listNotes(): Promise<Note[]> {
  return request<Note[]>("/notebook/");
}

export function createNote(
  content: string,
  section?: string | null,
  source?: { label: string; path: string },
): Promise<Note> {
  return request<Note>("/notebook/", {
    method: "POST",
    body: {
      content,
      section: section ?? null,
      source_label: source?.label ?? null,
      source_path: source?.path ?? null,
    },
  });
}

/** The tabs the learner actually has. The default tab is unnamed and is
 *  never in this list. */
export function listSections(): Promise<string[]> {
  return request<{ sections: string[] }>("/notebook/sections").then((r) => r.sections);
}

/** Move a note to another tab; `null` puts it back in the default. */
export function moveNote(id: string, section: string | null): Promise<Note> {
  return request<Note>(`/notebook/${id}`, {
    method: "PATCH",
    body: { section, move_section: true },
  });
}

export function updateNote(id: string, content: string): Promise<Note> {
  return request<Note>(`/notebook/${id}`, { method: "PATCH", body: { content } });
}

export function deleteNote(id: string): Promise<void> {
  return request<void>(`/notebook/${id}`, { method: "DELETE" });
}
